import { useCallback, useState } from 'react';
import { useAuthStore } from '@/features/auth/auth.store';

/**
 * Comparisons a reader has named, to come back to.
 *
 * A view is nothing but the page's query string: the report's whole state is
 * already in the URL, so saving one is keeping a link and opening one is
 * following it. That also means a saved view with no dates in it stays
 * relative — "the last thirty days" each time it is opened — because the URL
 * only carries dates the reader actually picked.
 *
 * Kept in this browser, per account. It is a convenience for one person and
 * loses nothing that cannot be rebuilt, so it does not earn a table and two
 * endpoints; the cost is that views do not follow the reader to another
 * machine, and a view worth sharing is shared by sending its link.
 */

export interface SavedView {
  id: string;
  name: string;
  /** The query string, without the leading `?`. */
  search: string;
}

const MAX_VIEWS = 30;
const MAX_NAME = 60;

const keyFor = (userId: string) => `usage-analytics.saved-views.${userId}`;

function read(userId: string | undefined): SavedView[] {
  if (!userId) return [];
  try {
    const parsed: unknown = JSON.parse(localStorage.getItem(keyFor(userId)) ?? '[]');
    if (!Array.isArray(parsed)) return [];
    // Checked rather than trusted: this is whatever was left in storage, by
    // any build of the page, and a name is about to be rendered.
    return parsed.filter(
      (entry): entry is SavedView =>
        Boolean(entry) &&
        typeof entry.id === 'string' &&
        typeof entry.name === 'string' &&
        typeof entry.search === 'string'
    );
  } catch {
    // Blocked, private, or corrupt. The page works without it.
    return [];
  }
}

export function useSavedViews() {
  const userId = useAuthStore((s) => s.profile?.id);
  const [views, setViews] = useState(() => read(userId));

  const write = useCallback(
    (next: SavedView[]) => {
      setViews(next);
      if (!userId) return;
      try {
        localStorage.setItem(keyFor(userId), JSON.stringify(next));
      } catch {
        // Full or blocked: the list still holds for this visit, which is
        // better than refusing to save at all.
      }
    },
    [userId]
  );

  return {
    views,
    full: views.length >= MAX_VIEWS,
    save: (name: string, search: string) => {
      const trimmed = name.trim().slice(0, MAX_NAME);
      if (!trimmed) return;
      // Saving under a name already in use replaces it: that is what a reader
      // retyping the name means, and two views called the same thing cannot
      // be told apart in the list.
      const others = views.filter(
        (view) => view.name.toLowerCase() !== trimmed.toLowerCase()
      );
      write(
        [
          { id: `${Date.now().toString(36)}`, name: trimmed, search },
          ...others,
        ].slice(0, MAX_VIEWS)
      );
    },
    remove: (id: string) => write(views.filter((view) => view.id !== id)),
  };
}
